import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtTime } from "@/lib/api";

const LEVEL = { critical: "text-red-400", warning: "text-amber-400", success: "text-emerald-400", info: "text-sky-300" };

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" aria-label="Notifications" className="relative rounded-sm border border-[#1E2D4A] p-2 text-slate-300 transition-colors hover:border-[#E5202E]/50 hover:text-white">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -right-1.5 -top-1.5 rounded-full bg-[#E5202E] px-1.5 font-mono text-[10px] font-bold text-white">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-[#1E2D4A] bg-[#0B1120] p-0">
        <div className="flex items-center justify-between border-b border-[#1E2D4A] px-4 py-3">
          <span className="rg-label">Alerts</span>
          <button data-testid="notifications-read-all" onClick={readAll} className="text-xs text-[#E5202E] hover:underline">Mark all read</button>
        </div>
        <ul data-testid="notifications-list" className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <li className="px-4 py-6 text-sm text-slate-500">No alerts yet. Connection changes and health failures appear here.</li>}
          {data.items.map((n) => (
            <li key={n.id} className={`border-b border-[#1E2D4A]/60 px-4 py-3 ${n.read ? "opacity-60" : ""}`}>
              <div className={`text-sm font-semibold ${LEVEL[n.level] || "text-slate-200"}`}>{n.title}</div>
              <div className="mt-0.5 break-words text-xs text-slate-400">{n.message}</div>
              <div className="mt-1 font-mono text-[10px] text-slate-600">{n.community_id || "system"} · {fmtTime(n.created_at)}</div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
